import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteCreate } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import { partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteContext from "./default-class-website-context"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
}

export default function createClassWebsiteAction(payload: Payload): PreparedAction {
  return {
    name: "CREATE_CLASS_WEBSITE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let createWebsitePatch = Patch.create("class-website", payload.websiteId)
    .isCreatePatch()
    .set("attributes.title", "My Fantastic Website")
    .set("attributes.slug", `my-fantastic-website-url-${payload.websiteId}`)
    .set("attributes.unitsArePublic", false)
    .set("attributes.isDeleted", false)
    .set("relationships.planbook.data", {
      id: payload.planbookId,
      type: "planbook",
    }).patch

  let planbookPatch = Patch.create("planbook", payload.planbookId).push(
    "relationships.classWebsites.data",
    {
      id: payload.websiteId,
      type: "class-website",
    }
  ).patch

  return [createWebsitePatch, planbookPatch]
}

function undoPatches(payload: Payload) {
  let planbookPatch = Patch.create("planbook", payload.planbookId).pull(
    "relationships.classWebsites.data",
    {
      id: payload.websiteId,
      type: "class-website",
    }
  ).patch

  return [planbookPatch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let newEvent: ClassWebsiteCreate = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "create",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: null,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],
  }

  return {
    title: "Created Class Website",
    titleForAnalytics: "Class Website - Created",
    description: "",
    context: defaultClassWebsiteContext(payload, models),
    activity: defaultClassWebsiteActivity(payload, models, "created"),
    newAnalyticsEvents: [newEvent],
  }
}
